import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/LockOutlined";
import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactElement, ReactNode, useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { CostType, Stage } from "../../../shared/CaseConfiguration";
import {
  NamedEntity,
  OfferStatus,
  SettlementCosts,
} from "../../../shared/CaseModel";
import { SettlementBasis } from "../../../shared/settlementCaps";
import { settlementCostsTotal } from "../../../shared/settlementCosts";
import { CostInputDialog } from "../../costs/CostInputDialog";
import {
  computeWaterfall,
  ensureApportionmentItems,
  getCoveredQuantums,
} from "../../../shared/apportionmentEngine";
import {
  ApportionmentItem,
  isCostsItem,
  isQuantumItem,
  itemKey,
} from "../../../shared/apportionmentTypes";
import { CurrencyTextField } from "../../general/CurrencyTextField";
import { Sx, TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { DialogShell } from "../../general/DialogShell";
import { SectionLabel } from "../../general/SectionLabel";
import { TonalBadge } from "../../general/TonalBadge";
import ApportionmentList from "./ApportionmentList";
import ApportionmentSummary from "./ApportionmentSummary";
import { OfferStatusBadge, isDecided, statusKey } from "./offerStatus";
import { apportionmentContextOf } from "./settlementModel";

/** What a side's spent costs offer its settlements, and the VAT rate it pays its costs with. */
export type SideBasis = { basis: SettlementBasis; vatRate?: number };

export interface Offer {
  id: string;
  // party proposing offer (could be receiving or paying)
  offeror: NamedEntity;
  // party receiving offer (not necessarily the money, could be payer)
  offeree: NamedEntity;
  payment: { amount: number; offereePays: boolean };
  stage: Stage;
  status: OfferStatus;
  // the day it was accepted (see SettlementOffer)
  acceptedOn?: string;
  costs: {
    settlement: { offeror: SettlementCosts; offeree: SettlementCosts };
  };
  // its place among the case's offers, which orders settlements of one stage
  order: number;
  // what each side's spent costs offer a settlement in which it receives the
  // sum, and the VAT it pays its costs with (see settlementCaps)
  bases: { offeror: SideBasis; offeree: SideBasis };
  claims: {
    id: string;
    // the place of the claim on the Claims page, counted from 1
    number: number;
    title: string;
    settledByOffer: boolean;
    quantumIds: string[];
  }[];
  quantums: {
    id: string;
    title: string;
    value: number;
  }[];
  apportionment: ApportionmentItem[];
}

export interface SettlementOfferDialogProps {
  initialOffer: Offer;
  // if positive, offeror pays offeree
  open: boolean;
  // signed bargaining midpoint per stage, from the offeror's perspective
  // (positive ⇒ offeree pays offeror); turned into a directional suggestion
  // under the amount field for the selected stage
  midpointByStage?: { readonly [stage: number]: number };
  onClose: (offer?: Offer) => void;
}

/** Room for what is offered and what it is paid towards side by side, in px. */
const WIDTH = 880;

/** How strongly a chosen option and a suggestion are washed with the primary colour. */
const TINT_ALPHA = 0.04;

const KEY_PREFIX = "case.analytics.settlement.offerDialog";

/** The label over a control that shows none of its own. */
const FieldLabel = ({ id, children }: { id?: string; children: ReactNode }) => (
  <Typography
    id={id}
    variant="caption"
    component="div"
    sx={{ mb: 0.625, fontWeight: 600, color: "text.secondary" }}
  >
    {children}
  </Typography>
);

/** One of the groups of what an offer consists of: its label, what is meant by it, and its controls. */
const Section = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) => (
  <Box component="section">
    <SectionLabel component="h3">{label}</SectionLabel>
    {description && (
      <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
        {description}
      </Typography>
    )}
    <Box sx={{ mt: description ? 1 : 1.25 }}>{children}</Box>
  </Box>
);

/** The cost types a side's settlement costs are entered in, in this order. */
const SETTLEMENT_COST_TYPES: CostType[] = [
  "lawFirmFee",
  "barristerFee",
  "courtFee",
  "expertFee",
  "otherDisbursement",
];

/**
 * A side's settlement costs: what they come to, entered type by type in the
 * dialog the Costs page enters costs in.
 */
const SettlementCostsField = ({
  label,
  stage,
  costs,
  disabled,
  onChange,
}: {
  label: string;
  // the name of the offer's stage, which they are spent at
  stage: string;
  costs: SettlementCosts;
  disabled: boolean;
  onChange: (costs: SettlementCosts) => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: KEY_PREFIX });
  const { currency } = useCurrency();
  const [editing, setEditing] = useState(false);
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <Button
        variant="outlined"
        color="inherit"
        fullWidth
        disabled={disabled}
        onClick={() => setEditing(true)}
        endIcon={<EditIcon fontSize="small" />}
        aria-label={t("costs.edit", { label })}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          fontWeight: 600,
          ...TABULAR_NUMERALS,
        }}
      >
        {currency.format(settlementCostsTotal(costs))}
      </Button>
      {editing && (
        <CostInputDialog
          open
          title={label}
          subTitle={stage}
          submitLabel={t("costs.save")}
          costs={Object.fromEntries(
            SETTLEMENT_COST_TYPES.map((type) => [type, costs[type] ?? 0]),
          )}
          onClose={(updated) => {
            setEditing(false);
            if (updated)
              onChange(
                Object.fromEntries(
                  Object.entries(updated).filter(
                    ([, amount]) => (amount ?? 0) > 0,
                  ),
                ),
              );
          }}
        />
      )}
    </div>
  );
};

/** Says what of the offer is fixed, and what makes it open to change again. */
const FixedNote = ({ children, sx }: { children: ReactNode; sx?: Sx }) => (
  <Box
    sx={[
      {
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        bgcolor: "action.hover",
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <LockIcon
      sx={{ flex: "none", mt: 0.25, fontSize: 16, color: "text.secondary" }}
    />
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

/**
 * One of the options of a choice, in a box of its own: the chosen one is
 * outlined. An option of a choice that is fixed keeps its outline, in grey.
 */
const Option = ({
  value,
  chosen,
  disabled,
  control,
  alignTop = false,
  children,
}: {
  // tells the options of a choice of one apart
  value?: string;
  chosen: boolean;
  disabled: boolean;
  control: ReactElement;
  // the control stays at the first line of a text of several
  alignTop?: boolean;
  children: ReactNode;
}) => {
  const { theme } = useTheme();

  return (
    <FormControlLabel
      value={value}
      control={control}
      disabled={disabled}
      // what is fixed is still to be read: the text keeps its ink
      disableTypography
      label={children}
      sx={{
        m: 0,
        pr: 1.5,
        alignItems: alignTop ? "flex-start" : "center",
        border: 1,
        borderRadius: 2,
        borderColor: chosen
          ? disabled
            ? "action.disabled"
            : "primary.main"
          : "divider",
        bgcolor: chosen
          ? disabled
            ? "action.hover"
            : alpha(theme.palette.primary.main, TINT_ALPHA)
          : "background.paper",
      }}
    />
  );
};

/**
 * A settlement offer of one party to another: the sum and who pays it, the
 * claims it settles, what reaching it costs, and what the sum is paid towards.
 * What of it can be changed follows its status: all of a draft; of an offer
 * that has been made only the apportionment; of an accepted or a rejected one
 * nothing.
 */
const SettlementOfferDialog = ({
  initialOffer,
  open,
  onClose,
  midpointByStage,
}: SettlementOfferDialogProps) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: KEY_PREFIX });
  const { t: tApp } = useTranslation();
  const directionLabelId = useId();

  const [offer, setOffer] = useState(initialOffer);
  const {
    status,
    stage,
    offeree,
    offeror,
    payment: { offereePays, amount },
    claims,
    quantums,
    costs,
  } = offer;
  // what is offered is fixed once the offer is made; its apportionment only
  // once the offer is accepted or rejected
  const offerFixed = status !== OfferStatus.Draft;
  const allFixed = isDecided(status);
  const payer = offereePays ? offeree : offeror;

  // Bumped whenever we set the amount programmatically (e.g. applying the
  // midpoint suggestion) to remount CurrencyTextField, which otherwise keeps
  // its own internal value and ignores external `value` changes.
  const [amountFieldNonce, setAmountFieldNonce] = useState(0);

  const covered = getCoveredQuantums(claims, quantums);
  const idsOfCoveredQuantums = covered.map((q) => q.id);

  // sync apportionment items with current covered quantums (adds/removes as claims change)
  const apportionment = ensureApportionmentItems(
    offer.apportionment ?? [],
    idsOfCoveredQuantums,
  );

  const close = () => onClose();
  const submit = () => onClose(offer);

  // what the sum can be paid towards, capped as the calculation caps it
  const waterfallCtx = apportionmentContextOf(offer);
  const waterfallAmounts = computeWaterfall(apportionment, waterfallCtx);
  const amountRemaining = waterfallAmounts.remaining;

  const claimLabel = (claim: Offer["claims"][number]) =>
    tApp("case.claims.number", { ordinal: claim.number });
  // a claimed amount is named with its value, so its title is not read as a loss or a gain
  const claimedAmountsOf = ({ quantumIds }: Offer["claims"][number]) => {
    const claimed = quantumIds.flatMap(
      (quantumId) => quantums.find(({ id }) => id === quantumId) ?? [],
    );
    return claimed.length === 0
      ? t("claims.noAmounts")
      : t("claims.amounts", {
          count: claimed.length,
          amounts: claimed
            .map(({ title, value }) =>
              t("claims.amount", { title, value: currency.format(value) }),
            )
            .join(" · "),
        });
  };

  // Build labels and subtitles for ApportionmentList
  const apportionmentLabels: Record<string, string> = {};
  const apportionmentSubtitles: Record<string, string> = {};
  for (const item of apportionment) {
    const key = itemKey(item);
    if (isCostsItem(item)) {
      switch (item.costs) {
        case "ofSettlement":
          // capped by the settlement costs of the side that receives the sum,
          // of its offers with the other side (see settlementCaps)
          apportionmentLabels[key] = t("costs.settlement");
          break;
        case "towardsPayer":
          apportionmentLabels[key] = t("costs.towardsPayer", {
            opponent: payer.name,
          });
          break;
        case "generic":
          apportionmentLabels[key] = t("costs.other");
          break;
      }
    } else if (isQuantumItem(item)) {
      const quantum = quantums.find((q) => q.id === item.quantumId);
      apportionmentLabels[key] = quantum?.title ?? item.quantumId;
      const claimNames = claims
        .filter(
          (c) => c.settledByOffer && c.quantumIds.includes(item.quantumId),
        )
        .map((claim) =>
          t("claims.name", { claim: claimLabel(claim), title: claim.title }),
        );
      if (claimNames.length > 0) {
        apportionmentSubtitles[key] = t("apportionment.claimedIn", {
          claims: claimNames.join(", "),
        });
      }
    }
  }

  // informational only, does not block saving
  const apportionmentWarning =
    amountRemaining > 0
      ? t("warning.notApportioned", {
          amount: currency.format(amountRemaining),
        })
      : amountRemaining < 0
        ? t("warning.exceedsOffer", {
            amount: currency.format(-amountRemaining),
          })
        : undefined;
  // blocks saving; an offer of which nothing changes any more is not saved
  const claimError =
    !allFixed && !claims.some((claim) => claim.settledByOffer)
      ? t("error.noClaimSelected")
      : undefined;
  const message = claimError
    ? { tone: theme.palette.data.negative, text: claimError }
    : apportionmentWarning
      ? { tone: theme.palette.warning, text: apportionmentWarning }
      : undefined;

  // Directional bargaining-midpoint suggestion for the selected stage. The
  // sign of the (offeror-perspective) midpoint decides who pays whom; the
  // amount is always its magnitude, so we never suggest a negative payment.
  const signedMidpoint = midpointByStage?.[stage];
  const midpointSuggestion =
    signedMidpoint !== undefined && signedMidpoint !== 0
      ? {
          amount: Math.abs(signedMidpoint),
          // offeror is the net receiver when its midpoint is positive
          offereePays: signedMidpoint > 0,
        }
      : undefined;
  const applyMidpointSuggestion = () => {
    if (!midpointSuggestion) return;
    setOffer({
      ...offer,
      payment: {
        amount: midpointSuggestion.amount,
        offereePays: midpointSuggestion.offereePays,
      },
    });
    setAmountFieldNonce((n) => n + 1);
  };

  const setSettlementCosts = (
    of: "offeror" | "offeree",
    newCosts: SettlementCosts,
  ) =>
    setOffer({
      ...offer,
      costs: { ...costs, settlement: { ...costs.settlement, [of]: newCosts } },
    });

  const columnSx = { minWidth: 0, px: 2.5, pt: 2, pb: 2.5 };

  return (
    <DialogShell
      open={open}
      onClose={close}
      width={WIDTH}
      sectioned
      title={
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 1.25,
            rowGap: 0.5,
          }}
        >
          {t("title", { offeror: offeror.name })}
          <OfferStatusBadge status={status} />
        </Box>
      }
      description={t("description", { offeree: offeree.name })}
      actions={
        <>
          <Box role="status" sx={{ flex: 1, minWidth: 0 }}>
            {message && (
              <TonalBadge
                tone={message.tone}
                sx={{ py: 0.5, whiteSpace: "normal" }}
              >
                <WarningIcon fontSize="inherit" />
                {message.text}
              </TonalBadge>
            )}
          </Box>
          {allFixed ? (
            <Button onClick={close} variant="contained" autoFocus>
              {tApp("dialog.close")}
            </Button>
          ) : (
            <>
              <Button onClick={close} color="inherit">
                {t("cancel")}
              </Button>
              <Button
                onClick={submit}
                variant="contained"
                autoFocus
                disabled={claimError !== undefined}
              >
                {t("submit")}
              </Button>
            </>
          )}
        </>
      }
    >
      {/* the head of the dialog leaves the room under it to its content */}
      <Box sx={{ mt: 1.75 }}>
        {allFixed && (
          <FixedNote
            sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: "divider" }}
          >
            {t(`fixed.${statusKey(status)}`)}
          </FixedNote>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(2, minmax(0, 1fr))",
            },
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {/* What is offered */}
          <Box
            sx={{
              ...columnSx,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {offerFixed && !allFixed && (
              <FixedNote sx={{ px: 1.5, py: 1.25, borderRadius: 2 }}>
                {t(`fixed.${statusKey(status)}`)}
              </FixedNote>
            )}

            <Section label={t("sections.payment.offer.title")}>
              <FieldLabel id={directionLabelId}>
                {t("direction.label")}
              </FieldLabel>
              <RadioGroup
                aria-labelledby={directionLabelId}
                value={offereePays ? "offeree" : "offeror"}
                onChange={(_, newPayer) =>
                  setOffer({
                    ...offer,
                    payment: {
                      ...offer.payment,
                      offereePays: newPayer === "offeree",
                    },
                  })
                }
                sx={{ gap: 0.75 }}
              >
                {(
                  [
                    ["offeree", offeree, offeror],
                    ["offeror", offeror, offeree],
                  ] as const
                ).map(([value, paying, receiving]) => (
                  <Option
                    key={value}
                    value={value}
                    chosen={paying.id === payer.id}
                    disabled={offerFixed}
                    control={<Radio size="small" />}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        color:
                          paying.id === payer.id
                            ? "text.primary"
                            : "text.secondary",
                      }}
                    >
                      <Trans
                        i18nKey={`${KEY_PREFIX}.direction.pays`}
                        values={{
                          payer: paying.name,
                          receiver: receiving.name,
                        }}
                        components={{ bold: <b /> }}
                      />
                    </Typography>
                  </Option>
                ))}
              </RadioGroup>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.75,
                  mt: 1.75,
                }}
              >
                <div>
                  <FieldLabel>{t("amount")}</FieldLabel>
                  <CurrencyTextField
                    key={`amount-${amountFieldNonce}`}
                    currency={currency}
                    ariaLabel={t("amount")}
                    onValueChange={(newAmount) =>
                      setOffer({
                        ...offer,
                        payment: { ...offer.payment, amount: newAmount },
                      })
                    }
                    required
                    variant="outlined"
                    size="small"
                    min={0}
                    value={amount}
                    disabled={offerFixed}
                    sx={{ width: "100%" }}
                  />
                </div>
              </Box>

              {/* the midpoint of the bargaining range at the stage, to take over */}
              {!offerFixed && midpointSuggestion && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.25,
                    mt: 1.25,
                    pl: 1.5,
                    pr: 0.75,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, TINT_ALPHA),
                  }}
                >
                  <Typography variant="body2" sx={TABULAR_NUMERALS}>
                    {t("amountMidpoint", {
                      stage: t(`stages.${Stage[stage]}`),
                      payer: midpointSuggestion.offereePays
                        ? offeree.name
                        : offeror.name,
                      receiver: midpointSuggestion.offereePays
                        ? offeror.name
                        : offeree.name,
                      amount: currency.format(midpointSuggestion.amount),
                    })}
                  </Typography>
                  <Button
                    size="small"
                    onClick={applyMidpointSuggestion}
                    sx={{
                      flex: "none",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {t("applyMidpoint")}
                  </Button>
                </Box>
              )}
            </Section>

            <Section
              label={t("sections.claims.title")}
              description={t("sections.claims.description")}
            >
              {claims.length === 0 ? (
                <Typography variant="body2">{t("claims.none")}</Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {claims.map((claim) => (
                    <Option
                      key={claim.id}
                      chosen={claim.settledByOffer}
                      disabled={offerFixed}
                      alignTop
                      control={
                        <Checkbox
                          size="small"
                          checked={claim.settledByOffer}
                          onChange={(_, settledByOffer) =>
                            setOffer({
                              ...offer,
                              claims: claims.map((other) =>
                                other.id === claim.id
                                  ? { ...other, settledByOffer }
                                  : other,
                              ),
                            })
                          }
                        />
                      }
                    >
                      <Box component="span" sx={{ minWidth: 0, py: 0.875 }}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            color: "text.secondary",
                            "& b": { fontWeight: 700, color: "text.primary" },
                          }}
                        >
                          <Trans
                            i18nKey={`${KEY_PREFIX}.claims.option`}
                            values={{
                              claim: claimLabel(claim),
                              title: claim.title,
                            }}
                            components={{ bold: <b /> }}
                          />
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            ...TABULAR_NUMERALS,
                          }}
                        >
                          {claimedAmountsOf(claim)}
                        </Typography>
                      </Box>
                    </Option>
                  ))}
                </Box>
              )}
            </Section>

            <Section
              label={t("sections.payment.costs.title")}
              description={t("sections.payment.costs.description")}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.75,
                }}
              >
                {(
                  [
                    ["offeror", offeror],
                    ["offeree", offeree],
                  ] as const
                ).map(([of, party]) => (
                  <SettlementCostsField
                    key={of}
                    label={t(`costs.${of}`, { name: party.name })}
                    stage={t(`stages.${Stage[stage]}`)}
                    costs={costs.settlement[of]}
                    disabled={offerFixed}
                    onChange={(newCosts) => setSettlementCosts(of, newCosts)}
                  />
                ))}
              </Box>
            </Section>
          </Box>

          {/* What the sum is paid towards: open to change until the offer is accepted or rejected */}
          <Box
            sx={{
              ...columnSx,
              bgcolor: "background.default",
              // beside what is offered in a wide window, under it in a narrow one
              borderLeft: { md: `1px solid ${theme.palette.divider}` },
              borderTop: {
                xs: `1px solid ${theme.palette.divider}`,
                md: "none",
              },
              // dragging a row would otherwise select the text it passes
              userSelect: "none",
            }}
          >
            <Section
              label={t("apportionment.title")}
              description={t(
                `apportionment.hint.${allFixed ? "decided" : statusKey(status)}`,
              )}
            >
              <Box sx={{ mt: 0.5 }}>
                <ApportionmentSummary
                  settlementAmount={amount}
                  amounts={waterfallAmounts}
                  currency={currency}
                />
                <ApportionmentList
                  items={apportionment}
                  context={waterfallCtx}
                  onItemsChange={(newItems) => {
                    if (allFixed) return;
                    setOffer({ ...offer, apportionment: newItems });
                  }}
                  currency={currency}
                  labels={apportionmentLabels}
                  subtitles={apportionmentSubtitles}
                  readOnly={allFixed}
                />
              </Box>
            </Section>
          </Box>
        </Box>
      </Box>
    </DialogShell>
  );
};

export default SettlementOfferDialog;
